import createStyles from "@mui/styles/createStyles";
import makeStyles from "@mui/styles/makeStyles";
import { getBackendErrors, getErrorMessage } from "api/errors";
import { useReward } from "api/rewards";
import { TextInput } from "components/FormControls/TextInput";
import { OLModal } from "components/Tooltips/OLModal";
import { OLTheme } from "contexts/Theme/ThemeProvider";
import { useNotifications } from "contexts/Toast/ToastProvider";
import { Form, Formik } from "formik";
import { useMessage } from "hooks/intl";
import { OLButton } from "OLButton";
import { backendErrors } from "utilities/backendErrors";
import { focusError } from "utilities/focusError";

const useStyles = makeStyles((theme: OLTheme) =>
  createStyles({
    input: {
      maxWidth: "100%",
      width: 406,
      "& + &": { marginTop: theme.spacing(10) },
    },
    button: {
      marginTop: theme.spacing(12),
    },
    error: {
      color: theme.red500,
      display: "block",
      fontSize: 14,
      marginBottom: theme.spacing(8),
      marginTop: theme.spacing(5),
      "& + &": { marginTop: 0 },
    },
    dragDropText: {
      color: theme.gray400,
      fontSize: 13,
      opacity: "0.8",
    },
    dragDropWrapper: {
      margin: 0,
    },
    fullWidth: {
      margin: 0,
      maxWidth: "100%",
      width: "100%",
    },
    downloadLink: {
      color: theme.primary500,
      display: "inline-block",
      marginBottom: theme.spacing(8),
      textDecoration: "none",
    },
  }),
);

const AddCoupon = ({
  closeModal,
  open,
  rewardCode,
}: {
  closeModal: () => void;
  open: boolean;
  rewardCode: string;
}) => {
  const classes = useStyles();
  const { addCoupon } = useReward();
  const { message } = useMessage();
  const { notify } = useNotifications();

  return (
    <OLModal
      header={message({ id: "addCoupon" })}
      onClose={closeModal}
      open={open}
    >
      <Formik
        initialStatus={{}}
        initialValues={{ coupon: "" }}
        onSubmit={async (values, { setSubmitting, setStatus }) => {
          setSubmitting(true);
          setStatus({});
          try {
            const response = await addCoupon(values.coupon, rewardCode);
            if (response) {
              closeModal();
              notify(message({ id: "couponAdded" }));
            }
          } catch (exception) {
            setSubmitting(false);
            notify(getErrorMessage(exception));
            const errors = backendErrors(getBackendErrors(exception));
            setStatus(errors);
            focusError();
          }
        }}
      >
        {({ handleSubmit, isSubmitting, status }) => {
          const formError = status?.[""];
          return (
            <Form onSubmit={handleSubmit}>
              {formError && <span className={classes.error}>{formError}</span>}
              <TextInput
                className={classes.input}
                label={message({ id: "coupon" })}
                name="coupon"
                data-test-id="coupon"
              />
              <OLButton
                className={classes.button}
                color="primary"
                disabled={isSubmitting}
                fullWidth
                type="submit"
                variant="contained"
                data-test-id="add-coupon"
              >
                {message({ id: "addCoupon" })}
              </OLButton>
            </Form>
          );
        }}
      </Formik>
    </OLModal>
  );
};

export default AddCoupon;
